'use client';

import React from 'react';
import { classNames } from '@repo/ui/src/utils/classNames';
import '@pqina/flip/dist/flip.min.css';

interface FlipProps {
  value?: number | string;
  startValue?: number;
  intervalMs?: number;
  minDelta?: number;
  maxDelta?: number;
  autoTick?: boolean;
}

export default class Flip extends React.Component<FlipProps> {
  private _tickRef = React.createRef<HTMLDivElement>();
  private _tickInstance: any;
  private _intervalId: ReturnType<typeof setInterval> | null = null;
  private _Tick: any = null;

  static defaultProps: Partial<FlipProps> = {
    startValue: 0,
    intervalMs: 2500,
    minDelta: 1,
    maxDelta: 10,
    autoTick: true,
  };

  private clearTimer = () => {
    if (this._intervalId) {
      clearInterval(this._intervalId);
      this._intervalId = null;
    }
  };

  private startAutoTick = () => {
    const { autoTick = true, intervalMs = 2500, minDelta = 1, maxDelta = 10 } = this.props;
    if (!this._tickInstance || !autoTick) return;

    this.clearTimer();

    const min = Math.floor(Number.isFinite(minDelta) ? minDelta : 1);
    const max = Math.floor(Number.isFinite(maxDelta) ? maxDelta : Math.max(min, 10));
    const ms = Math.max(0, intervalMs);

    this._intervalId = setInterval(() => {
      const delta = Math.floor(Math.random() * (max - min + 1)) + min;
      this._tickInstance.value = Number(this._tickInstance.value || 0) + delta;
    }, ms);
  };

  async componentDidMount() {
    if (!this._tickRef.current) return;

    try {
      const TickModule = await import('@pqina/flip');
      this._Tick = TickModule.default;
    } catch (error) {
      console.error('Failed to load @pqina/flip:', error);
      return;
    }

    const { value, startValue = 0, autoTick = true } = this.props;

    const initial = value !== undefined ? value : startValue;

    const didInit = (tick: any) => {
      this._tickInstance = tick;

      if (value !== undefined) {
        tick.value = value;
      }

      if (autoTick) this.startAutoTick();
    };

    // Create once
    this._tickInstance = this._Tick.DOM.create(this._tickRef.current, {
      value: initial,
      didInit,
    });
  }

  componentDidUpdate(prevProps: FlipProps) {
    if (!this._tickInstance) return;

    const { value, intervalMs, minDelta, maxDelta, autoTick } = this.props;

    if (value !== undefined && prevProps.value !== value) {
      this._tickInstance.value = value;
    }

    if (
      prevProps.intervalMs !== intervalMs ||
      prevProps.minDelta !== minDelta ||
      prevProps.maxDelta !== maxDelta ||
      prevProps.autoTick !== autoTick
    ) {
      this.clearTimer();
      if (autoTick) this.startAutoTick();
    }
  }

  componentWillUnmount() {
    this.clearTimer();
    if (this._tickInstance?.destroy) {
      this._tickInstance.destroy();
      this._tickInstance = null;
    }
  }

  render() {
    return (
      <div ref={this._tickRef} className={classNames('text-4xl font-normal md:text-6xl')}>
        <div data-repeat="true" aria-hidden="true">
          <span
            data-view="flip"
            className={classNames(
              // Base styles
              'mx-1 w-8 rounded-sm tracking-widest indent-1.5 min-w-0',
              // Custom responsive class
              'flip-counter-responsive',
              // Standard responsive
              'md:w-15 md:h-28 md:flex md:items-center',
            )}
          ></span>
        </div>
      </div>
    );
  }
}
